import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { PrimeNGModule } from '../../../primeng.module';
import { AppInfoService } from '../../../services/app-info/app-info.service';
import { MetaTagsService } from '../../../services/meta-tags/meta-tags.service';
import { InfoCategory } from '../../models/app/info-category';
import InfoSubCategory from '../../models/product/info-sub-category';
import { MenuMobileComponent } from '../menu-mobile/menu-mobile.component';

@Component({
  selector: 'app-menu-bar',
  standalone: true,
  imports: [PrimeNGModule, MenuMobileComponent],
  templateUrl: './menu-bar.component.html',
  styleUrl: './menu-bar.component.scss',
})
export class MenuBarComponent {
  public categories: InfoCategory[] = [];
  public items: MenuItem[] = [];
  public isMobile = false;
  public mobileMenuOpen = false;
  public mobileSubmenus: Record<string, boolean> = {};

  constructor(
    private appInfoService: AppInfoService,
    private metaTagService: MetaTagsService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const normalize = () => {
      const items = document.querySelectorAll('li.p-menuitem');

      items.forEach((el) => {
        const x = el as HTMLElement;
        x.removeAttribute('aria-label');
        x.removeAttribute('aria-level');
        x.removeAttribute('aria-setsize');
        x.removeAttribute('aria-posinset');
      });
    };

    const menu = document.querySelector('.p-menubar');

    if (menu) {
      const observer = new MutationObserver(() => normalize());
      observer.observe(menu, { childList: true, subtree: true });
      normalize();
    }
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 640;
    } else {
      this.isMobile = false;
    }

    this.categories =
      this.appInfoService.getCategoriesWithProductActive() || [];

    this.items = [
      ...this.categories.map((category: InfoCategory) => {
        const catId = category.id ?? '';
        const catDesc = category.description ?? '';
        const catLink = catId.replaceAll('_', '-').toLowerCase();

        return {
          id: catId,
          label: catDesc,
          url: '#',
          command: (e) => e.originalEvent?.preventDefault(),
          styleClass: `menu-cat-${catLink}`,
          items: (category.subCategories ?? []).map((sub: InfoSubCategory) => {
            const subId = sub.id ?? '';
            const subDesc = sub.description ?? '';
            const subLink = subId.replaceAll('_', '-').toLowerCase();

            return {
              id: subId,
              label: subDesc,
              routerLink: `/${catLink}/${subLink}`,
              styleClass: `menu-sub-${subId}`,
              command: () => this.takeMenuAction(),
            };
          }),
        } as MenuItem;
      }),
    ];
  }

  takeMenuAction() {
    this.metaTagService.classToBody('overflow-hidden-menu', false);
  }

  onClickMenu(e: MouseEvent) {
    if (!this.isMobile) return;
    e.preventDefault();
    this.mobileMenuOpen = true;
    this.metaTagService.classToBody('overflow-hidden-menu', true);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.metaTagService.classToBody(
      'overflow-hidden-menu',
      this.mobileMenuOpen
    );
  }

  toggleSubmenu(id: string) {
    this.mobileSubmenus[id] = !this.mobileSubmenus[id];
  }

  go(path: string) {
    this.mobileMenuOpen = false;
    this.metaTagService.classToBody('overflow-hidden-menu', false);
    this.router.navigateByUrl(path);
  }

  addOverflowHiddenToBody(add: boolean) {
    this.metaTagService.classToBody('overflow-hidden-menu', add);
  }
}
